import {
  Badge,
  Card,
  CardContent,
  CardHeader,
} from '@growdash/design-system/atoms';
import { Branch } from '@growdash/growintel/generated';
import {
  useCurrency,
  useGrowintelService,
  useParams,
} from '@growdash/growintel/hooks';
import { useQuery } from '@tanstack/react-query';
import { Clock, Loader2, MapPin, Star } from 'lucide-react';
import { useState } from 'react';

function RestaurantListCard({
  branch,
  isSelected,
  onClick,
}: {
  branch: Branch;
  isSelected: boolean;
  onClick: () => void;
}) {
  if (!branch) return null;
  const currency = useCurrency();

  const cuisineList = branch?.cuisines.split(', ');
  return (
    <Card
      className={`cursor-pointer transition-all duration-200 hover:shadow-md animate-fade-in ${
        isSelected ? 'ring-1 ring-support-orange shadow-md' : ''
      }`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex gap-4">
          <img
            src={branch.branch_image || '/placeholder.svg'}
            alt={branch.branch_name}
            className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
          />

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg mb-1 truncate">
              {branch.branch_name}
            </h3>

            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">
                {branch.area}, {branch.city}
              </span>
            </div>

            <div className="flex flex-wrap gap-1 mb-2">
              {cuisineList.slice(0, 2).map((cuisine, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {cuisine.trim()}
                </Badge>
              ))}
              {cuisineList.length > 2 && (
                <Badge variant="secondary" className="text-xs">
                  +{cuisineList.length - 2}
                </Badge>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-medium text-sm">
                    {branch.rating_score}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ({branch.rating_count})
                  </span>
                </div>

                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">{branch.delivery_time} min</span>
                </div>
              </div>

              <div className="flex items-center gap-1 text-sm">
                <span className="font-medium">
                  {currency} {branch.avg_price}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BranchDetailCard({ branch }: { branch: Branch }) {
  if (!branch) return null;

  const currency = useCurrency();
  const cuisineList = branch?.cuisines.split(', ');
  return (
    <Card className="h-fit animate-fade-in">
      <CardHeader>
        <h2 className="text-2xl font-bold">{branch.branch_name}</h2>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="relative">
          <img
            src={branch.branch_image || '/placeholder.svg'}
            alt={branch.branch_name}
            className="w-full h-48 object-cover rounded-lg"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-5">
            <div>
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Location
              </h4>
              <div className="flex items-center gap-2 mt-1">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="font-medium">{branch.area}</p>
                  <p className="text-sm text-muted-foreground">{branch.city}</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Rating
              </h4>
              <div className="flex items-center gap-2 mt-1">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span className="font-bold">{branch.rating_score}</span>
                <span className="text-muted-foreground text-sm">
                  ({branch.rating_count} reviews)
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Pricing
              </h4>
              <div className="mt-1">
                <div className="flex items-center gap-1">
                  <span className="font-bold">
                    {currency} {branch.avg_price}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {` • Average`}
                  </span>
                </div>
                <div>
                  <span className="font-bold">
                    {currency} {branch.median_price}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {` • Median`}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Delivery Time
              </h4>
              <div className="flex items-center gap-2 mt-1">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-lg font-medium">
                  {branch.delivery_time} minutes
                </span>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-2">
            Cuisines
          </h4>
          <div className="flex flex-wrap gap-2">
            {cuisineList.map((cuisine, index) => (
              <Badge key={index} variant="secondary">
                {cuisine.trim()}
              </Badge>
            ))}
          </div>
        </div>

        <div className="pt-4 border-t">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Country:</span>
              <span className="font-medium"> {branch.country_code}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Last Update:</span>
              <span className="font-medium"> {branch.scrape_month}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function BranchesView() {
  const params = useParams();

  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

  const { data: branches, isFetching } = useQuery({
    queryKey: ['brand-branches', params.id],
    queryFn: async (): Promise<Branch[] | undefined | null> => {
      if (!params.id) return null;
      const response =
        await service?.branchesAPI.getBranchByBrandIdBranchesBrandBrandIdGet(
          params.id,
        );
      return response?.data.items;
    },
  });

  const service = useGrowintelService();

  if (isFetching)
    return (
      <div className="w-full flex justify-center py-24 animate-pulse">
        <Loader2 className="animate-spin" />
      </div>
    );
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-4 py-8">
        <div
          className={`flex gap-8 ${
            selectedBranch ? 'lg:flex-row' : 'flex-col'
          }`}
        >
          <div className={`${selectedBranch ? 'lg:w-1/2' : 'w-full'}`}>
            <div className="space-y-4">
              {branches?.map((b: Branch) => (
                <RestaurantListCard
                  key={b.branch_id}
                  branch={b}
                  isSelected={selectedBranch?.branch_id === b.branch_id}
                  onClick={() => setSelectedBranch(b)}
                />
              ))}
            </div>
          </div>

          {selectedBranch && (
            <div className="lg:w-1/2">
              <BranchDetailCard branch={selectedBranch} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
